/**
 * Google Analytics 4 integration for the AUTHENTICATED app - activation-
 * funnel milestone events only. Deliberately a separate module from
 * utils/analytics.ts (marketing pages only), so each file's privacy
 * contract stays independently simple to audit rather than one file
 * carrying two different rules for two different parts of the app.
 *
 * Deliberately gated by its OWN env var (VITE_PRODUCT_ANALYTICS_ID), not
 * VITE_GA_MEASUREMENT_ID - the two must never share a var. Marketing-site
 * visitor counting (utils/analytics.ts) is expected to be turned on;
 * in-app activity tracking is a separate decision this app must not make
 * silently just because someone configured the public one. If you want
 * in-app milestones flowing into the same GA4 property as the public site,
 * set VITE_PRODUCT_ANALYTICS_ID to the same measurement ID explicitly -
 * don't merge these two vars back into one.
 *
 * Hard rule, same spirit as utils/analytics.ts: every event fired from here
 * is one of the fixed names below, with NO free-form parameters - no asset
 * tags/names, no employee names/emails, no company name, no user-typed
 * text, ever. Only "this milestone happened" plus, where noted, a small
 * non-identifying integer count.
 *
 * These events fire on every occurrence (not just the first) - they exist
 * to show where in the funnel sessions drop off (e.g. how many sessions
 * that logged in ever created an asset), not to compute "activation" by
 * themselves. Activation itself (>=5 assets and >=1 assignment for a
 * tenant) is measured directly from the product database, which is exact,
 * rather than reconstructed from client-side event sequencing, which
 * isn't reliable enough for a real business metric. See
 * docs/PRODUCT_ANALYTICS.md.
 *
 * No-ops entirely if VITE_PRODUCT_ANALYTICS_ID isn't set.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const measurementId = import.meta.env.VITE_PRODUCT_ANALYTICS_ID as string | undefined;
let initialized = false;

function ensureLoaded() {
  if (!measurementId || initialized) return;
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // Reuses window.gtag if utils/analytics.ts already defined it earlier in
  // this session (e.g. the user visited a marketing page before signing
  // in) - gtag.js tolerates being configured more than once.
  window.gtag = window.gtag || function gtag(...args: unknown[]) { window.dataLayer!.push(args); };
  window.gtag("js", new Date());
  window.gtag("config", measurementId, { send_page_view: false, anonymize_ip: true });
}

export type ProductMilestone =
  | "login"
  | "return_visit"
  | "employee_created"
  | "asset_created"
  | "assignment_created"
  | "qr_scan"
  | "report_viewed";

/** Fires a product-usage funnel event with zero identifying payload - see
 * the module doc comment for the hard rule this exists to enforce. `count`
 * may only be a small non-identifying integer (e.g. the tenant's total
 * asset count after this action), never anything else. */
export function trackProductMilestone(milestone: ProductMilestone, count?: number) {
  if (!measurementId) return;
  ensureLoaded();
  window.gtag?.("event", milestone, count === undefined ? {} : { milestone_count: count });
}

const RETURN_VISIT_KEY = "assetops_has_logged_in_before";

/** Call once per authenticated session start (e.g. AppLayout mount). Fires
 * "login" the first time this browser has ever seen an authenticated
 * session, "return_visit" every time after - a simple, honest per-browser
 * signal, not a cross-device "first login ever" guarantee. */
export function trackLoginOrReturnVisit() {
  const hasVisitedBefore = localStorage.getItem(RETURN_VISIT_KEY) === "1";
  trackProductMilestone(hasVisitedBefore ? "return_visit" : "login");
  if (!hasVisitedBefore) localStorage.setItem(RETURN_VISIT_KEY, "1");
}
